import * as UserAction from '@ori-events/user-action';
import { ArrowUpThin } from '@ori-ui/icons';
import { SHADOW_LEVEL1, styled } from '@ori-ui/mui';
import { useIsMobile } from '@ori/presentation-hooks';
import useTestId from '@ori/testid-generator';
import type { MouseEvent, RefObject, FC } from 'react';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BASE_ANALYTICS_HIERARCHY } from '../constants';
import { TranslationKeys, type NavigationItem } from '../models';
import { useTopAreaStore } from '../stores/TopAreaStore';
import { findCurrentSubpagePath } from '../utils';
import { BackButton } from './BackButton';
import { SubpageLink } from './SubpageLink';

interface StyledNavigationProps {
  ownerState: {
    isNested: boolean;
  };
}

interface StyledNavigationWrapperProps {
  ownerState: {
    isToggled: boolean;
  };
}

const StyledNavigation = styled('div')<StyledNavigationProps>(({ ownerState: { isNested }, theme }) => ({
  boxSizing: 'border-box',
  marginBottom: 'unset',
  minHeight: '60px',
  '& *': {
    boxSizing: 'border-box',
  },
  '& svg': {
    fontSize: 'unset',
    marginRight: 'unset',
  },
  [theme.breakpoints.up(768)]: {
    marginBottom: 'unset',
    minHeight: 'unset',
  },
  ...(isNested && {
    fontSize: theme.typography.pxToRem(19),
    marginBottom: theme.spacing(8),
    marginRight: theme.spacing(1),
    minHeight: '120px',
    [theme.breakpoints.up(768)]: {
      marginBottom: 0,
      minHeight: 'auto',
    },
  }),
}));

const StyledNavigationWrapper = styled('ul')<StyledNavigationWrapperProps>(({ ownerState: { isToggled }, theme }) => ({
  background: theme.palette.common.white,
  borderRadius: '0 0 15px 15px',
  boxShadow: SHADOW_LEVEL1,
  display: 'flex',
  flexDirection: 'column',
  listStyle: 'none',
  margin: theme.spacing(0, 0, 4),
  maxHeight: 'unset',
  overflow: 'hidden',
  padding: theme.spacing(1),
  position: 'absolute',
  transition: '300ms',
  width: '100%',
  zIndex: 101,
  ...(isToggled && {
    borderRadius: 0,
    maxHeight: '60px',
    [theme.breakpoints.up(768)]: {
      maxHeight: 'none',
    },
  }),
  [theme.breakpoints.up(768)]: {
    borderRadius: 0,
    boxShadow: 'none',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: theme.spacing(0, 'auto'),
    maxHeight: 'unset',
    maxWidth: '1100px',
    overflow: 'unset',
    padding: 0,
    position: 'relative',
  },
}));

const StyledBackButtonWrapper = styled('li')({
  display: 'flex',
});

const StyledHeader = styled('li')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  display: 'flex',
  flex: '0 0 auto',
  fontSize: theme.typography.body1.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  height: '60px',
  marginBottom: theme.spacing(4),
  order: 1,
  padding: theme.spacing(0, 4),
  [theme.breakpoints.up(768)]: {
    display: 'none',
    height: '30px',
    margin: theme.spacing(0, 3),
    order: 'unset',
    padding: 0,
  },
}));

const StyledChild = styled('li')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  display: 'none',
  flex: '0 0 auto',
  fontSize: theme.typography.body1.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(4),
  order: 2,
  padding: theme.spacing(0, 4),
  [theme.breakpoints.up(768)]: {
    display: 'flex',
    height: '30px',
    margin: theme.spacing(0, 3),
    order: 'unset',
    padding: 0,
  },
}));

const StyledHeaderText = styled('a')<StyledNavigationProps>(({ ownerState: { isNested }, theme }) => ({
  alignItems: 'unset',
  color: 'inherit',
  display: 'unset',
  marginRight: 'auto',
  overflow: 'hidden',
  textDecoration: 'none',
  textOverflow: 'ellipsis',
  width: '100%',
  '&:hover': {
    color: theme.palette.text.secondary,
    marginRight: 'unset',
  },
  ...(isNested && {
    alignItems: 'center',
    display: 'flex',
  }),
}));

const StyledChildText = styled('a')(({ theme }) => ({
  color: 'inherit',
  overflow: 'hidden',
  textDecoration: 'none',
  textOverflow: 'ellipsis',
  width: '100%',
  '&:hover': {
    color: theme.palette.text.secondary,
  },
}));

const StyledArrowUpThin = styled(ArrowUpThin)<StyledNavigationWrapperProps>(({ ownerState: { isToggled }, theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: theme.typography.subtitle1.fontSize,
  marginLeft: theme.spacing(4),
  transition: '300ms',
  transform: 'unset',
  [theme.breakpoints.up(768)]: {
    display: 'none',
  },
  ...(isToggled && {
    color: theme.palette.text.secondary,
    transform: 'rotate(180deg)',
  }),
}));

export const Navigation: FC = () => {
  const { subPages, setIsSubnavNested } = useTopAreaStore();
  const [translate] = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isSubnavNestedLocal, setIsSubnavNestedLocal] = useState(false);
  const [navPath, setNavPath] = useState<NavigationItem[]>([]);
  const { getTestId } = useTestId();
  const contentRef: RefObject<HTMLUListElement> = useRef<HTMLUListElement>(null);
  const currentSubpages = navPath.length ? navPath.at(-1)?.Children : subPages;
  const isMobile = useIsMobile();

  useEffect(() => {
    const currentSubpagePathTmp = findCurrentSubpagePath(subPages);
    if (currentSubpagePathTmp.length) {
      const newNavPath = currentSubpagePathTmp.slice(0, -1);
      setNavPath(newNavPath);
      setIsSubnavNestedLocal(!(isMobile && newNavPath.length < 2 && currentSubpagePathTmp.length <= 2));
    }
  }, [subPages, isMobile]);

  useEffect(() => {
    setIsSubnavNested(isSubnavNestedLocal);
  }, [isSubnavNestedLocal, setIsSubnavNested]);

  const toggle = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      setIsExpanded(!isExpanded);
    },
    [isExpanded],
  );

  const selectSubpage = useCallback(
    (event: MouseEvent, subPage: NavigationItem) => {
      UserAction.dispatch([...BASE_ANALYTICS_HIERARCHY, subPage.Url, subPage.Label]);

      if (!subPage.Url) {
        event.preventDefault();

        setNavPath([...navPath, subPage]);
        setIsSubnavNestedLocal(true);
      }
    },
    [navPath],
  );

  const goBackAction = useCallback(() => {
    const newNavPath = navPath.slice(0, -1);

    if ((isMobile && !newNavPath.length) || (!isMobile && !newNavPath.length)) {
      setIsSubnavNestedLocal(false);
    }
    setNavPath(newNavPath);
  }, [navPath, isMobile]);

  if (currentSubpages?.length == null) {
    return null;
  }

  const parentItem = navPath.length ? navPath.at(-1) : null;
  const currentItem = currentSubpages.find((subpage) => subpage.IsCurrent) ?? parentItem;
  const hasChildren = Boolean(currentItem?.Children.length);
  const ownerState = { isNested: isSubnavNestedLocal, isToggled: !isExpanded };

  const placeholder: NavigationItem = {
    Label: translate(TranslationKeys.selectPage),
    Url: '',
    IsCurrent: true,
    Children: [],
  };

  const subNavItems = () => {
    if (isMobile) {
      if (!currentSubpages.some((sub) => sub.IsCurrent)) {
        return [placeholder, ...currentSubpages];
      }

      if (parentItem && hasChildren && navPath.length) {
        return [parentItem, ...currentSubpages];
      }
    }

    return currentSubpages;
  };

  return (
    <StyledNavigation
      ownerState={ownerState}
      data-testid={getTestId('navigation')}
    >
      {isMobile && parentItem && isSubnavNestedLocal ? (
        <BackButton
          label={parentItem.Label}
          onClick={goBackAction}
        />
      ) : null}
      <StyledNavigationWrapper
        ref={contentRef}
        ownerState={ownerState}
      >
        {parentItem && !isMobile ? (
          <StyledBackButtonWrapper>
            <BackButton
              label={parentItem.Label}
              onClick={goBackAction}
            />
          </StyledBackButtonWrapper>
        ) : null}
        {subNavItems().map((subPage) => {
          if (subPage.IsCurrent) {
            return (
              <Fragment key={subPage.Url}>
                <StyledHeader
                  data-testid={getTestId('navlink-self')}
                  onClick={toggle}
                >
                  <StyledHeaderText
                    ownerState={ownerState}
                    href={subPage.Url}
                  >
                    {subPage.Label}
                  </StyledHeaderText>
                  <StyledArrowUpThin ownerState={ownerState} />
                </StyledHeader>
                <StyledChild data-testid={getTestId('navlink-self')}>
                  <StyledChildText href={subPage.Url}>{subPage.Label}</StyledChildText>
                </StyledChild>
              </Fragment>
            );
          }

          return (
            <SubpageLink
              key={`${subPage.Label}-${subPage.Url}`}
              subpage={subPage}
              onClick={selectSubpage}
            />
          );
        })}
      </StyledNavigationWrapper>
    </StyledNavigation>
  );
};
